import React, { Component } from "react";
import styled from "styled-components";
import DropZone from "react-dropzone";
import {
    FlexContainer,
    BackgroundContainer,
    BrowseFileButton,
    CustomiseStepButton,
    CustomiseStepContent,
    CustomiseStepShell,
    PortalActionBar,
    PortalCaption,
    PortalMetricCard,
    PortalMetricLabel,
    PortalPanelHeader,
    PortalSectionTitle,
    portalColors
} from "src/components";

import ImageSlider from "../components/ImageSlider";
import { CustomiseContext } from "../CustomiseContext";
import GridBackground from "src/img/GridBackground.png";

const UploadContainer = styled.div`
    width: 100%;
`;

const UploadSize = {
    width: "100%",
    maxWidth: 405,
    height: 102,
    marginBottom: "0.85rem",
    marginTop: "0.85rem",
    boxSizing: "border-box"
};

export const PhoneContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    flex: 1 1 350px;
    min-width: min(100%, 350px);
    max-width: 100%;
    margin-top: 1rem;
`;

const ButtonsContainer = styled.div`
    display: contents;
`;

const UploadInstructionCard = styled.div`
    display: grid;
    gap: 0.25rem;
    padding: 0;
    background: transparent;
    border: none;
    box-shadow: none;
    margin-top: 1rem;
`;

const UploadPreviewCard = styled(PortalMetricCard)`
    width: min(100%, 27.5rem);
    min-height: auto;
    align-content: start;
    padding: 0.9rem;
`;

const PreviewFrame = styled.div`
    width: min(100%, 405px);
    min-height: 102px;
    display: grid;
    place-items: center;
    border: 1px ${p => p.$empty ? "dashed" : "solid"} ${portalColors.borderStrong};
    border-radius: var(--calendar-radius-input, 8px);
    overflow: hidden;
    background: ${portalColors.surfaceSoft};
    box-sizing: border-box;

    img {
        max-width: 100%;
        max-height: 102px;
    }
`;

const UploadProgress = styled.div`
    width: min(100%, 391px);
    height: 10px;
    background: ${portalColors.borderStrong};
    border-radius: 999px;
    overflow: hidden;
`;

const UploadProgressFill = styled.div`
    width: ${p => p.$percentage || 0}%;
    height: 100%;
    background: ${portalColors.text};
`;

class UploadLogo extends Component {
    state = {
        dropzoneActive: false,
        maxSize: 5242880, //5MB

        isUploading: false,
        uploadPercentage: 0,

        errorMessage: "",
    };

    static contextType = CustomiseContext;

    /**
     * File dropped into logo drop zone, validate file type and upload to S3
     * @param droppedFiles {Array<File>}
     */
    onDrop = (droppedFiles) => {
        const fileTypeErrorMessage = "Your logo must be of the PNG or JPEG format, and less than 5MB in size.";
        // If the drop zone receives anything other than the accepted formats, or anything greater
        // than the max file size specified, the droppedFiles array will be empty.
        if (droppedFiles.length === 0) {
            this.setState({
                errorMessage: fileTypeErrorMessage
            });
            return;
        }

        // Take first file if multiple provided and use that
        let fileToUpload = droppedFiles[0];

        const validImageTypes = ["image/jpeg", "image/png"];
        if (!validImageTypes.includes(fileToUpload.type)) {
            this.setState({
                errorMessage: fileTypeErrorMessage
            });
            return;
        }

        this.context.nextLogoUploading();
        this.setState({
            errorMessage: ""
        });

        const preview = URL.createObjectURL(fileToUpload);

        // Keep the draft local until final save so cancelled/replaced files never create tracked storage rows.
        this.context.uploadFile("logoFile", droppedFiles.map(oneFile => ({
            ...oneFile,
            fileToUpload: fileToUpload,
            preview: preview
        })));

        this.context.nextLogoAvailable(preview);
    };

    clickNext = () => {
        if (this.context.isNextClickable) {
            this.context.nextClick();
        } else {
            this.setState({
                errorMessage: "Please upload a company logo first"
            });
        }
    };

    render() {
        const { maxSize, isUploading, uploadPercentage, errorMessage } = this.state;
        const { logoFile, assetLogoUrl, currentLogoUrl, companyName, currentScreen, currentForm, screenIds, colourPalette,
            leftScreen, rightScreen } = this.context;

        return (
            <>
                <CustomiseStepShell>
                    <CustomiseStepContent>
                        <PortalPanelHeader withDivider>
                            <div>
                                <PortalSectionTitle>Step 1: Upload Your Logo</PortalSectionTitle>
                                <PortalCaption margin="0.25rem 0 0">
                                    Start by uploading your business logo. Check the device preview to see where your logo will appear in the app.
                                </PortalCaption>
                            </div>
                        </PortalPanelHeader>

                        <FlexContainer style={{ flexShrink: 0 }}>
                            <UploadPreviewCard>
                                <PortalMetricLabel>Upload your logo</PortalMetricLabel>

                                <DropZone
                                    accept="image/png, image/jpeg"
                                    maxSize={maxSize}
                                    onDrop={this.onDrop.bind(this)}
                                    style={UploadSize}
                                >
                                    <UploadContainer>
                                        {isUploading ?
                                            <>
                                                {uploadPercentage === 100 ?
                                                    <PreviewFrame>
                                                        <BackgroundContainer width="100%" height="102px" image={GridBackground}>
                                                            <img src={logoFile[0].preview} alt={"Logo File Preview"} />
                                                        </BackgroundContainer>
                                                    </PreviewFrame>
                                                    :
                                                    <FlexContainer width="100%" height="102px">
                                                        <PortalCaption margin="0">
                                                            Uploading - {uploadPercentage}%
                                                        </PortalCaption>
                                                        <UploadProgress>
                                                            <UploadProgressFill $percentage={uploadPercentage} />
                                                        </UploadProgress>
                                                    </FlexContainer>
                                                }
                                            </>
                                            :
                                            <>
                                                {logoFile.length === 0 && assetLogoUrl === "" ?
                                                    <PreviewFrame $empty>
                                                        <img src={GridBackground} alt="UploadBackground" width="405px" />
                                                    </PreviewFrame> :
                                                    <PreviewFrame>
                                                        <BackgroundContainer width="100%" height="102px" image={GridBackground}>
                                                            {logoFile.length === 0 ?
                                                                <img src={assetLogoUrl} alt={"Asset Logo Url"} />
                                                                :
                                                                <img src={logoFile[0].preview} alt={"Logo File"} />
                                                            }
                                                        </BackgroundContainer>
                                                    </PreviewFrame>
                                                }
                                            </>
                                        }
                                    </UploadContainer>
                                </DropZone>

                                <BrowseFileButton onChange={(files) => this.onDrop(files)}>
                                    Browse File
                                </BrowseFileButton>
                            </UploadPreviewCard>

                            <UploadInstructionCard>
                                <PortalMetricLabel>Image Format</PortalMetricLabel>
                                <PortalCaption margin="0">
                                    PNG File - Transparent Background
                                </PortalCaption>
                            </UploadInstructionCard>

                            <PortalCaption margin="0">
                                Click <b>Next </b> to proceed to <b>Step 2:</b> Colour Selection.
                            </PortalCaption>

                            <PortalActionBar
                                justifyContent="flex-start"
                                padding="1rem 0 0"
                                border="transparent"
                                shadow="none"
                                radius="0"
                            >
                                <ButtonsContainer>
                                    <CustomiseStepButton
                                        onClick={() => this.context.backToCustomise() }
                                    >
                                        Back
                                    </CustomiseStepButton>

                                    <CustomiseStepButton
                                        $variant="primary"
                                        onClick={ this.clickNext }
                                    >
                                        Next
                                    </CustomiseStepButton>
                                </ButtonsContainer>
                            </PortalActionBar>

                            <PortalCaption color={portalColors.danger} margin="0">
                                {errorMessage}
                            </PortalCaption>
                        </FlexContainer>
                    </CustomiseStepContent>
                    <PhoneContainer>
                        <ImageSlider
                            companyName={companyName}
                            logoTopOffset={45}
                            phoneFrameTopOffset={30}
                            currentScreen={currentScreen}
                            currentForm={currentForm}
                            screenIds={screenIds}
                            leftScreen={leftScreen}
                            rightScreen={rightScreen}
                            colourPalette={colourPalette}
                            assetLogoUrl={currentLogoUrl || assetLogoUrl} />
                    </PhoneContainer>
                </CustomiseStepShell>
            </>
        );
    }
}

export default UploadLogo;
